import { dropTask } from 'ember-concurrency';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import Button from 'tio-ui/components/button';
import Input from 'tio-ui/components/input';
import AlertBanner from 'tio-ui/components/alert-banner';
import IconBadge from 'tio-ui/components/icon-badge';
import {
  PhShieldCheck,
  PhDeviceMobile,
  PhArrowClockwise,
  PhArrowRight,
  PhInfo,
} from 'ember-phosphor-icons';
import type PostLoginService from 'tio-employee/services/post-login';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';

export default class PostLoginMfaSmsTemplate extends Component {
  @service declare postLogin: PostLoginService;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  @tracked code = '';
  @tracked errorMessage = '';
  @tracked resendMessage = '';
  @tracked codeErrors: string[] = [];

  handleCodeChange = (value: string) => {
    // Only allow digits, max 6
    this.code = value.replace(/\D/g, '').slice(0, 6);
    // Clear errors on new input
    if (this.codeErrors.length) {
      this.codeErrors = [];
    }
    if (this.errorMessage) {
      this.errorMessage = '';
    }
  };

  submitCode = dropTask(async () => {
    this.errorMessage = '';
    this.resendMessage = '';

    // Validate
    if (!this.code || this.code.length < 6) {
      this.codeErrors = [this.intl.t('login.mfa_sms.code_min_length')];
      return;
    }

    try {
      const accessToken = await this.postLogin.verifyMfa(this.code);
      this.postLogin.completeRequirement('mfa_sms');
      const nextRoute = this.postLogin.nextRouteName;
      if (nextRoute) {
        this.router.transitionTo(nextRoute);
        return;
      }
      await this.postLogin.completeAuthentication(accessToken);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : this.intl.t('login.mfa_sms.error_unknown');
      this.errorMessage = message;
    }
  });

  resendCode = dropTask(async () => {
    this.errorMessage = '';
    this.resendMessage = '';
    try {
      await this.postLogin.resendMfa();
      this.resendMessage = this.intl.t('login.mfa_sms.resend_success');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : this.intl.t('login.mfa_sms.error_unknown');
      this.errorMessage = message;
    }
  });

  handleSubmit = (event: Event) => {
    event.preventDefault();
    this.submitCode.perform();
  };

  <template>
    <div
      class="min-h-screen bg-linear-to-br from-gray-50 via-white to-violet-50 tio-row-center px-4"
    >
      <div class="w-full max-w-md">
        {{! Card }}
        <div
          class="tio-bg-surface rounded-2xl shadow-lg shadow-violet-500/5 border tio-border-subtle overflow-hidden"
        >
          {{! Header gradient band }}
          <div class="tio-gradient-brand px-8 py-8 text-center relative overflow-hidden">
            {{! Decorative circles }}
            <div
              class="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10"
              aria-hidden="true"
            ></div>
            <div
              class="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-white/5"
              aria-hidden="true"
            ></div>

            <div class="relative">
              <div class="tio-flex-center mb-4">
                <div class="w-14 h-14 rounded-2xl bg-white/20 tio-row-center backdrop-blur-sm">
                  <PhShieldCheck class="tio-fg-inverse" @size={{28}} @weight="fill" />
                </div>
              </div>
              <h1 class="tio-text-xl-bold tio-fg-inverse tracking-tight">{{t
                  "login.mfa_sms.title"
                }}</h1>
              <p class="text-violet-200 text-sm mt-1.5">{{t "login.mfa_sms.subtitle"}}</p>
            </div>
          </div>

          {{! Body }}
          <div class="px-8 py-8 space-y-6">
            {{! Description with phone icon }}
            <div class="tio-row-top">
              <IconBadge @icon={{PhDeviceMobile}} @color="violet" @size="sm" />
              <p class="text-sm tio-fg-secondary leading-relaxed">{{t
                  "login.mfa_sms.description"
                }}</p>
            </div>

            {{! Success message }}
            {{#if this.resendMessage}}
              <AlertBanner @intent="success" data-test-mfa-resend-success>
                {{this.resendMessage}}
              </AlertBanner>
            {{/if}}

            {{! Error message }}
            {{#if this.errorMessage}}
              <AlertBanner @intent="warning" data-test-mfa-error>
                {{this.errorMessage}}
              </AlertBanner>
            {{/if}}

            {{! Code form }}
            <form {{on "submit" this.handleSubmit}}>
              <div class="space-y-5">
                <Input
                  @label={{t "login.mfa_sms.code_label"}}
                  @value={{this.code}}
                  @onChange={{this.handleCodeChange}}
                  @placeholder="000000"
                  @size="lg"
                  @inputmode="numeric"
                  @autocomplete="one-time-code"
                  @maxlength={{6}}
                  @errors={{this.codeErrors}}
                  @leadingIcon={{PhShieldCheck}}
                  data-test-mfa-code-input
                />

                {{! Buttons }}
                <div class="tio-col pt-1">
                  <Button
                    @type="submit"
                    @intent="primary"
                    @size="lg"
                    @isFullWidth={{true}}
                    @isRunning={{this.submitCode.isRunning}}
                    @icon={{PhArrowRight}}
                    @iconPlacement="end"
                    aria-label={{t "login.mfa_sms.submit"}}
                    data-test-mfa-submit
                  >
                    {{t "login.mfa_sms.submit"}}
                  </Button>

                  <Button
                    @intent="default"
                    @appearance="minimal"
                    @size="md"
                    @isFullWidth={{true}}
                    @isRunning={{this.resendCode.isRunning}}
                    @icon={{PhArrowClockwise}}
                    {{on "click" this.resendCode.perform}}
                    aria-label={{t "login.mfa_sms.resend"}}
                    data-test-mfa-resend
                  >
                    {{t "login.mfa_sms.resend"}}
                  </Button>
                </div>
              </div>
            </form>

            {{! Help tip }}
            <div
              class="tio-row-top-tight p-3 tio-surface-sunken border tio-border-subtle"
              data-test-mfa-help-tip
            >
              <PhInfo class="tio-fg-disabled shrink-0 mt-0.5" @size={{16}} />
              <p class="text-xs tio-fg-muted leading-relaxed">{{t "login.mfa_sms.help_tip"}}</p>
            </div>
          </div>
        </div>

        {{! Footer branding }}
        <p class="text-center text-xs tio-fg-disabled mt-6">
          {{t "login.mfa_sms.secured_by"}}
        </p>
      </div>
    </div>
  </template>
}
